import { createSelector } from 'reselect'
import {
  chain,
  filter,
  flatten,
  groupBy,
  isNil,
  orderBy,
  pickBy,
  sortBy,
} from 'lodash'
import { DateTime } from 'luxon'

import { filterNulls, ReduxState } from '@/utils/typeHelpers'
import {
  Transaction,
  TransactionCategoryIdentifier,
} from '@/reducers/admin/allTransactions.slice'
import { TRANSACTIONS_SORT_BY } from '@/reducers/finances/transactionFiltersReducer'
import {
  TransactionRuleCategoryType,
  TransactionRuleUse,
} from '@/reducers/admin/transactionRulesReducer'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'
import { addCurrencyArray } from '@/utils/currencyHelpers'
import { getCurrentUser } from '@/selectors/user.selectors'
import { isTransactionLocked } from './helpers'
import { getAllTransactionCategories } from '@/features/reports/reports.selectors'
import { TransactionCategoryState } from '@/features/reports/reports.slice'

import { selectActiveQuarterlyTaxEstimateDetails } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

export const getUsersTransactions = (state: ReduxState) =>
  state.transactions.byId

const getUsersTransactionsOrdered = (state: ReduxState) =>
  state.transactions.allIds
export const getByTransactionCategoryId = (state: ReduxState) =>
  state.transactions.byTransactionCategoryId

const getAdminAllTransactions = (state: ReduxState) =>
  state.admin.allTransactions

const getAdminTransactionsById = createSelector(
  getAdminAllTransactions,
  (allTransactionState) => allTransactionState.byId
)

export const selectAdminQueriedTransactionIds = createSelector(
  getAdminAllTransactions,
  (allTransactionState) => allTransactionState.queriedTransactionIds
)

// Returns transaction ids that can be bulk edited (ie not disabled)
export const selectEnabledAdminQueriedTransactionIds = createSelector(
  getAdminTransactionsById,
  selectAdminQueriedTransactionIds,
  (transactionMap, queriedTransactions) =>
    queriedTransactions.filter((id) => !transactionMap[id]?.confirmedAt)
)

export const getUserTransactionById = createSelector(
  [
    getUsersTransactions,
    (_: unknown, id: string | number | undefined) => id,
    (_: unknown, __: unknown, splitIndex?: number) => splitIndex,
  ],
  (userTransactions, id, splitIndex) => {
    if (!id || !userTransactions) return null
    if (
      typeof splitIndex === 'number' &&
      userTransactions[id]?.splitTransactions
    )
      return userTransactions[id].splitTransactions[splitIndex]
    return userTransactions[id]
  }
)

export const getUserNeedsClarificationTransactionsSelector = createSelector(
  [getUsersTransactions],
  (userTransactions) =>
    filter(
      userTransactions,
      (transaction) => transaction.requestedClarificationAt !== null
    )
)

const filterForYear = (year: string, transactions: Transaction[]) =>
  filter(
    transactions,
    (transaction) =>
      DateTime.fromISO(transaction.date, { zone: 'utc' }).year.toString() ===
      year
  )

// New selector that filters by year
export const getUserNeedsClarificationTransactionsByYearSelector =
  createSelector(
    [
      getUserNeedsClarificationTransactionsSelector,
      (_: unknown, year: string | undefined) => year,
    ],
    (needsClarificationTransactions, year) => {
      if (!year) return needsClarificationTransactions

      return filterForYear(year, needsClarificationTransactions)
    }
  )

const getUserUncategorizedAndNotPersonalTransactionsSelector = createSelector(
  [getUsersTransactions],
  (userTransactions) =>
    filter(
      userTransactions,
      (transaction) =>
        !transaction.transactionCategoryId && transaction.type !== 'personal'
    )
)

const getUserUncategorizedAndNotPersonalTransactionsWithoutUserNote =
  createSelector(
    [getUserUncategorizedAndNotPersonalTransactionsSelector],
    (uncategorizedTransactions) =>
      uncategorizedTransactions.filter((t) => !t.notes)
  )

export const getUserUncategorizedAndNotPersonalTransactionsWithoutUserNoteForYear =
  createSelector(
    [
      getUserUncategorizedAndNotPersonalTransactionsWithoutUserNote,
      (_: unknown, year: string | undefined) => year,
    ],
    (uncategorizedTransactions, year) =>
      year
        ? filterForYear(year, uncategorizedTransactions)
        : uncategorizedTransactions
  )

const getTransactionsByCategoryIdentifier = (
  userTransactions: { [key: string]: Transaction },
  allTransactionCategories: TransactionCategoryState,
  categoryIdentifier: TransactionCategoryIdentifier
) => {
  const allTransactionCategoriesArray = Object.values(allTransactionCategories)

  const transactionCategoryId = allTransactionCategoriesArray.find(
    (c) => c.identifier === categoryIdentifier
  )?.id

  if (transactionCategoryId) {
    return Object.values(userTransactions).filter(
      (t) => t.transactionCategoryId === transactionCategoryId
    )
  }

  return []
}

const selectTransactionsByCategoryIdentifier = createSelector(
  getUsersTransactions,
  getAllTransactionCategories,
  (_: unknown, categoryIdentifier: TransactionCategoryIdentifier) =>
    categoryIdentifier,
  getTransactionsByCategoryIdentifier
)

export const selectUserTransactionsByCategoryIdentifierForYear = createSelector(
  [
    selectTransactionsByCategoryIdentifier,
    (_: unknown, __: string, year?: string) => year,
  ],
  (transactions, year) =>
    year ? filterForYear(year, transactions) : transactions
)

/**
 * Selects all assets over $2,500 for a given year by combining
 * equipment, computer hardware, and furniture categories
 */
export const selectAssetsOver2500ForYear = createSelector(
  [
    getUsersTransactions,
    getAllTransactionCategories,
    (_: unknown, year: string) => year,
  ],
  (transactions, categories, year) => {
    const assets = [
      ...getTransactionsByCategoryIdentifier(
        transactions,
        categories,
        TransactionCategoryIdentifier.equipment_gte_2500
      ),
      ...getTransactionsByCategoryIdentifier(
        transactions,
        categories,
        TransactionCategoryIdentifier.computer_hardware_gte_2500
      ),
      ...getTransactionsByCategoryIdentifier(
        transactions,
        categories,
        TransactionCategoryIdentifier.furniture_gte_2500
      ),
    ]
    return year ? filterForYear(year, assets) : assets
  }
)

/**
 * Calculates the required receipt count for assets over $2,500
 * by counting transactions without receipts
 */
export const selectRequiredReceiptCountForYear = createSelector(
  [selectAssetsOver2500ForYear],
  (assetsOver2500) =>
    assetsOver2500.reduce<number>((count, transaction) => {
      if (
        (transaction?.receipts && transaction?.receipts.length === 0) ||
        !transaction?.receipts
      ) {
        return count + 1
      }
      return count
    }, 0)
)

export const getAllTransactionFilters = (state: ReduxState) =>
  state.transactionFilters

export const filterUserTransactions = createSelector(
  [
    getAllTransactionFilters,
    (_: unknown, transactionType: string | number) => transactionType,
    getUsersTransactions,
    getUsersTransactionsOrdered,
  ],
  (filters, transactionType, userTransactions, orderedTransactions) => {
    let transactionsList = filterNulls(
      orderedTransactions.map((transactionId) =>
        userTransactions[Number(transactionId)]
          ? userTransactions[Number(transactionId)]
          : null
      )
    )

    switch (transactionType) {
      case 'all':
        break

      case 'business':
        transactionsList = transactionsList.filter((transaction) => {
          return transaction.type && transaction.type === 'business'
        })
        break
      case 'personal':
        transactionsList = transactionsList.filter((transaction) => {
          return transaction.type && transaction.type === 'personal'
        })
        break
      case 'review':
        transactionsList = transactionsList.filter((transaction) => {
          return transaction.requestedClarificationAt
        })
        break

      default:
        break
    }

    if (filters.startDate) {
      const startDate = DateTime.fromFormat(
        filters.startDate,
        DATE_FORMATS_LUXON.API_INPUT,
        {
          zone: 'utc',
        }
      ).startOf('day')
      transactionsList = filter(transactionsList, (transaction) => {
        return DateTime.fromISO(transaction.date, { zone: 'utc' }) >= startDate
      })
    }
    if (filters.endDate) {
      const endDate = DateTime.fromFormat(
        filters.endDate,
        DATE_FORMATS_LUXON.API_INPUT,
        {
          zone: 'utc',
        }
      ).endOf('day')

      transactionsList = filter(
        transactionsList,
        (transaction) =>
          DateTime.fromISO(transaction.date, { zone: 'utc' }) <= endDate
      )
    }

    if (filters.transactionCategoryId) {
      const filterCatId =
        Number(filters.transactionCategoryId) === -1
          ? null
          : Number(filters.transactionCategoryId)

      transactionsList = filter(transactionsList, (transaction) => {
        // Include split transactions that have sub transaction with category
        if (transaction.splitTransactions?.length) {
          return transaction.splitTransactions.some(
            (splitTransaction) =>
              splitTransaction.transactionCategoryId === filterCatId
          )
        } else {
          return transaction.transactionCategoryId === filterCatId
        }
      })

      // Filter split transactions by category
      transactionsList = transactionsList.map((transaction) => ({
        ...transaction,
        splitTransactions: transaction.splitTransactions?.filter(
          (splitTransaction) =>
            splitTransaction.transactionCategoryId === filterCatId
        ),
      }))

      // If we're attempting to filter for uncategorized items don't items don't include personal transactions since
      if (!filterCatId) {
        transactionsList = transactionsList.filter(
          (transaction) => transaction.type !== 'personal'
        )
      }
    }

    if (filters.financialAccountId) {
      transactionsList = filter(
        transactionsList,
        (transaction) =>
          transaction.financialAccountId === filters.financialAccountId
      )
    }

    transactionsList = sortBy(transactionsList, (transaction) => {
      switch (filters.sortVal) {
        case TRANSACTIONS_SORT_BY.DATE:
          return new Date(transaction.date).getTime() * -1
        case TRANSACTIONS_SORT_BY.DESCRIPTION:
          return transaction.description.toLowerCase()
        case TRANSACTIONS_SORT_BY.AMOUNT:
          return transaction.amountInCents
        case TRANSACTIONS_SORT_BY.ACCOUNT:
          return transaction.financialAccountId
        case TRANSACTIONS_SORT_BY.TYPE:
          return transaction.type
        case TRANSACTIONS_SORT_BY.CATEGORY:
          if (transaction.type === 'personal') {
            return 'personal'
          }
          return transaction.transactionCategory?.name.toLowerCase()
        case TRANSACTIONS_SORT_BY.ID:
        default:
          return transaction.id
      }
    })

    if (filters.sortAsc) {
      transactionsList = transactionsList.reverse()
    }

    // Count length AFTER we filter but BEFORE we truncate by page
    const count = Object.keys(transactionsList).length

    // Now that we've safely counted, we can paginate
    if (filters.page) {
      transactionsList = transactionsList.slice(
        (Number(filters.page) - 1) * Number(filters.limit),
        Number(filters.page) * Number(filters.limit)
      )
    }

    return {
      transactions: transactionsList,
      count,
    }
  }
)

export const selectUncategorizedTransactionsNotNeedClarification =
  createSelector(
    getUserUncategorizedAndNotPersonalTransactionsWithoutUserNoteForYear,
    (transactions) =>
      transactions.filter((t) => t.requestedClarificationAt === null)
  )

export const distinctTransactionsByDateDESC = (transactions: Transaction[]) =>
  chain(transactions)
    .uniqBy((t) => t.id)
    .sortBy((t) => -new Date(t.date))
    .value()

export const getAllTransactions = (state: ReduxState) =>
  state.admin.allTransactions.byId

const getAllTransactionList = createSelector(
  getAllTransactions,
  (transactions) => Object.values(transactions)
)

export const getTransactionsByUserId = createSelector(
  getAllTransactionList,
  (_: unknown, userId: number | string | undefined) => userId,
  (transactions, userId) =>
    userId
      ? transactions.filter(
          (trans) => trans.userId.toString() === userId.toString()
        )
      : []
)

export const selectTransactionRulesById = (state: ReduxState) =>
  state.admin.transactionRules

export const selectTransactionRulesByType = createSelector(
  [selectTransactionRulesById, (_: unknown, type: string) => type],
  (rules, type) => {
    if (type === 'active') {
      return filter(rules, (rule) => rule?.archivedAt === null)
    } else {
      return rules
    }
  }
)

export const selectSimilarRules = createSelector(
  selectTransactionRulesById,
  (_: unknown, newRuleUse: TransactionRuleUse | undefined) => newRuleUse,
  (_: unknown, __: unknown, newRuleText: string | null | undefined) =>
    newRuleText,
  (allRules, newRuleUse, newRuleText) => {
    if (!newRuleUse || !newRuleText) {
      return []
    }

    const similarNameRoles = Object.values(allRules).filter(({ ruleText }) =>
      ruleText.toLowerCase().includes(newRuleText.toLowerCase())
    )

    if (newRuleUse === TransactionRuleUse.CATEGORIZATION) {
      return similarNameRoles
    }

    return similarNameRoles.filter(
      ({ ruleUse }) => ruleUse === TransactionRuleUse.CLARIFICATION
    )
  }
)

export const getGroupedImportedTransactionsForUser = createSelector(
  getTransactionsByUserId,
  (userTransactions) => {
    const importedTransactions = userTransactions.filter(
      (t): t is typeof t & { importedOn: string } => Boolean(t.importedOn)
    )

    return groupBy(importedTransactions, (transaction) =>
      DateTime.fromISO(transaction.importedOn, {
        zone: 'utc',
      }).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)
    )
  }
)

// Financial Accounts

//////////////////////////////
/// User Facing Selectors ////
//////////////////////////////d

export const getUserTransactionsSelector = createSelector(
  [
    getUsersTransactions,
    (_: unknown, args: { startDate: DateTime; endDate: DateTime }) => args,
  ],
  (userTransactions, args) => {
    const filteredTransactions = filter(userTransactions, (transaction) => {
      const transactionDateInTimezone = DateTime.fromISO(transaction.date, {
        zone: 'utc',
      })

      return (
        transactionDateInTimezone >= args.startDate &&
        transactionDateInTimezone <= args.endDate
      )
    })

    return sortBy(filteredTransactions, 'date')
  }
)

export const selectFilteredGroupedUserTransactions = createSelector(
  getUserTransactionsSelector,
  getAllTransactionCategories,
  (transactions, categories) => {
    const flattenedTransactions = transactions.flatMap((txn) => {
      if (txn.splitTransactions && txn.splitTransactions.length > 0) {
        return txn.splitTransactions
      }
      return txn
    })
    const groupedTransactions = groupBy(
      // Filter out transactions that shouldn't be shown
      flattenedTransactions.filter(
        (t) =>
          !t.deletedAt &&
          !t.splitAt &&
          t.type !== TransactionRuleCategoryType.personal
      ),
      'transactionCategoryId'
    )

    const groupedIncome = pickBy(
      groupedTransactions,
      (_, key) => categories[key]?.accountType === 'Income'
    )

    // Grouped transactions without a category are under 'null'.  Find those that are over 0
    const unCatIncome =
      groupedTransactions['null']?.filter(
        ({ amountInCents }) => amountInCents > 0
      ) || []

    // Don't add the category unless there are transactions in it
    if (unCatIncome.length) {
      groupedIncome['null'] = unCatIncome
    }

    const groupedExpenses = pickBy(
      groupedTransactions,
      (_, key) => categories[key]?.accountType === 'Expenses'
    )

    const unCatExpenses =
      groupedTransactions['null']?.filter(
        ({ amountInCents }) => amountInCents < 0
      ) || []

    if (unCatExpenses.length) {
      groupedExpenses['null'] = unCatExpenses
    }

    const totalIncome = addCurrencyArray(
      flatten(Object.values(groupedIncome)).map((trans) => trans.amountInCents)
    )
    const totalExpenses = addCurrencyArray(
      flatten(Object.values(groupedExpenses)).map(
        (trans) => trans.amountInCents
      )
    )

    return {
      groupedIncome,
      groupedExpenses,
      totalIncome,
      totalExpenses,
    }
  }
)

const NotFullyDeductibleExpenseCategories = [
  TransactionCategoryIdentifier.charitable_donation,
  TransactionCategoryIdentifier.gifts,
  TransactionCategoryIdentifier.loan_repayment,
  TransactionCategoryIdentifier.loan_repayment_car,
  TransactionCategoryIdentifier.meals_entertainment,
  TransactionCategoryIdentifier.payroll_life_insurance,
  TransactionCategoryIdentifier.taxes_citylocal_taxes,
]

type transactionType =
  | 'uncategorized'
  | 'uncategorizedNonPersonalTransactions'
  | 'fullyDeductibleBusinessExpenses'
  | 'businessIncome'
  | 'businessExpenses'
  | 'categorizedExpenses'
  | 'categorized'
  | 'needsReview'

export const filterTransactionsByType = (
  userTransactions: { [p: string]: Transaction },
  transactionType: transactionType
) => {
  switch (transactionType) {
    case 'uncategorized':
      return filter(
        userTransactions,
        (transaction) => transaction.transactionCategoryId === null
      )
    case 'businessIncome':
      return filter(
        userTransactions,
        (transaction) =>
          !isNil(transaction.transactionCategory) &&
          transaction.transactionCategory.accountType === 'Income' &&
          transaction.type === 'business'
      )
    case 'businessExpenses':
      return filter(
        userTransactions,
        (transaction) =>
          !isNil(transaction.transactionCategory) &&
          transaction.transactionCategory.accountType === 'Expenses' &&
          transaction.type === 'business'
      )
    case 'fullyDeductibleBusinessExpenses':
      return filter(
        userTransactions,
        (transaction) =>
          !isNil(transaction.transactionCategory) &&
          !NotFullyDeductibleExpenseCategories.includes(
            transaction.transactionCategory.identifier
          ) &&
          transaction.transactionCategory.accountType === 'Expenses' &&
          transaction.type === 'business'
      )
    case 'uncategorizedNonPersonalTransactions':
      return filter(
        userTransactions,
        (transaction) =>
          transaction.type !== 'personal' &&
          transaction.transactionCategoryId === null
      )
    case 'categorizedExpenses':
      return filter(
        userTransactions,
        (transaction) =>
          !isNil(transaction.transactionCategory) &&
          !isNil(transaction.transactionCategoryId) &&
          transaction.transactionCategory.accountType === 'Expenses' &&
          transaction.type === 'business'
      )
    case 'categorized':
      return filter(
        userTransactions,
        (transaction) =>
          !isNil(transaction.transactionCategory) &&
          !isNil(transaction.transactionCategoryId)
      )
    case 'needsReview':
      return filter(userTransactions, (transaction) =>
        Boolean(transaction.requestedClarificationAt)
      )
    default:
      return transactionType satisfies never
  }
}

export const getTransactionsByType = createSelector(
  [
    getUsersTransactions,
    (_: unknown, transactionType: transactionType) => transactionType,
  ],
  filterTransactionsByType
)

export const getTransactionsInYearRange = (
  transactions: Transaction[],
  year: string | number
) => {
  if (year === 'all') {
    return transactions
  }

  const yearStart = DateTime.fromObject(
    { year: Number(year) },
    { zone: 'utc' }
  ).startOf('year')

  const yearEnd = DateTime.fromObject(
    { year: Number(year) },
    { zone: 'utc' }
  ).endOf('year')

  return transactions.filter((transaction) => {
    const transactionDate = DateTime.fromISO(transaction.date)

    return transactionDate >= yearStart && transactionDate <= yearEnd
  })
}

export const getTransactionsByTypeAndYear = createSelector(
  getTransactionsByType,
  (_: unknown, __: unknown, year: string | number) => year,
  getTransactionsInYearRange
)

export const getTransactionsByCategory = createSelector(
  [
    getUsersTransactions,
    (_: unknown, transactionCategoryId: string | number | null | undefined) =>
      transactionCategoryId,
  ],
  (transactions, transactionCategoryId) => {
    if (!transactionCategoryId) return []
    let filteredTransactions = filter(
      transactions,
      (transaction) =>
        Number(transaction.transactionCategoryId) ===
        Number(transactionCategoryId)
    )
    filteredTransactions = orderBy(filteredTransactions, ['date'], ['desc'])
    return filteredTransactions
  }
)

export const selectTransactionsByCategoryAndYear = createSelector(
  getTransactionsByCategory,
  (_: unknown, __: unknown, year: string | number) => year,
  getTransactionsInYearRange
)

export const selectTransactionSplitInfoById = createSelector(
  getUsersTransactions,
  (_: unknown, transactionId: string | number) => transactionId,
  (_: unknown, __: unknown, parentId?: string | number) => parentId,
  (transactions, transactionId, parentId) => {
    if (!transactions || !parentId || !transactions[parentId]) return null
    const transaction = transactions[parentId]
    if (!transaction?.splitTransactions) return null
    const splitIndex = transaction.splitTransactions.findIndex(
      (transaction) => transaction.id === transactionId
    )
    return {
      splitIndex,
      totalSplits: transaction.splitTransactions.length,
    }
  }
)

/** Selects from user or admin transactions based on currentUser */
export const selectTransactionById = createSelector(
  [
    (state) =>
      getCurrentUser(state)?.admin
        ? getAdminTransactionsById(state)
        : getUsersTransactions(state),
    (_: unknown, transactionId: number) => transactionId,
  ],
  (transactions, transactionId) => transactions[transactionId]
)

export const selectTransactionsByIds = createSelector(
  getUsersTransactions,
  (_: unknown, transactionIds: number[]) => transactionIds,
  (transactions, transactionIds) =>
    filterNulls(transactionIds.map((id) => transactions[id]))
)

export const selectIsSplitTransaction = createSelector(
  selectTransactionById,
  (transaction) => {
    return (
      transaction?.splitTransactions &&
      transaction?.splitTransactions?.length > 0
    )
  }
)

/**
 * Child/split txns are not included in this selector.
 * Use splitFrom instead of id as input parameter.
 */
export const selectIsLockedTransaction = createSelector(
  selectTransactionById,
  getCurrentUser,
  (transaction, currentUser) => {
    return isTransactionLocked({
      date: transaction.date,
      userLockedYear: currentUser?.booksLockedForUserYear,
      adminLockedYear: currentUser?.booksLockedForAdminYear,
      isAdmin: currentUser?.admin,
    })
  }
)

export const selectTransactionsNeedReviewForQTE = createSelector(
  [
    getUserNeedsClarificationTransactionsSelector,
    selectActiveQuarterlyTaxEstimateDetails,
  ],
  (transactions, activeQuarterDetails) => {
    // We should always have activeQuarterDetails, but as a fallback,
    // just return all transactions that need review
    if (!activeQuarterDetails) {
      return {
        count: transactions.length,
        allReviewed: transactions.length === 0,
      }
    }
    const needsReview = transactions.filter((transaction) => {
      const transactionDate = isoToUTCDateTime(transaction.date)
      return (
        transactionDate >=
          isoToUTCDateTime(activeQuarterDetails.bookkeepingPeriodStartsAt) &&
        transactionDate <=
          isoToUTCDateTime(activeQuarterDetails.bookkeepingPeriodEndsAt)
      )
    })
    return { count: needsReview.length, allReviewed: needsReview.length === 0 }
  }
)

/** used to get the transactions that need review for a specific month formatted as 'yyyy-MM'
 * count used to alert the user in the monthly reports page **/
export const selectTransactionsNeedReviewForMonth = createSelector(
  getUserNeedsClarificationTransactionsSelector,
  (_: unknown, month: string | undefined) => month,
  (transactions, month) => {
    if (!month) {
      return null
    }
    const needsReview = transactions.filter((transaction) => {
      const transactionDate = DateTime.fromISO(transaction.date)
      return (
        transactionDate >=
          DateTime.fromFormat(month, DATE_FORMATS_LUXON.YEAR_MONTH).startOf(
            'month'
          ) &&
        transactionDate <=
          DateTime.fromFormat(month, DATE_FORMATS_LUXON.YEAR_MONTH).endOf(
            'month'
          )
      )
    })
    return { count: needsReview.length, transactions: needsReview }
  }
)
